import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchDeletionJob,
  fetchDeletionPlan,
  fetchOpenDeletions,
  resumeAppDeletion,
  startAppDeletion,
} from "../helpers/fetch-app-deletion";
import {
  AppDeletionJob,
  DeletionPlan,
  DeletionStep,
  DeletionSystem,
} from "../types/app-deletion";
import { BORDER, INK, MUTED, Pill } from "./admin-ui";

const POLL_INTERVAL_MS = 1500;
const SYSTEM_ORDER: DeletionSystem[] = ["keycloak", "coolify", "registry"];

export interface AppDeletionTarget {
  appId: string;
  appName: string;
  clientName: string;
}

/** Keeps the latest callback without making effects depend on its identity. */
function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * Open (running or failed) deletions, polled while any runs so every admin on
 * the page sees progress. Calls `onFinished` when a deletion leaves the list.
 */
export function useOpenAppDeletions(enabled: boolean, onFinished: () => void) {
  const [jobs, setJobs] = useState<AppDeletionJob[]>([]);
  const [reloads, setReloads] = useState(0);
  const onFinishedRef = useLatest(onFinished);
  const anyRunning = jobs.some((job) => job.status === "running");

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = () =>
      void fetchOpenDeletions().then((result) => {
        if (!cancelled && "data" in result) setJobs(result.data);
      });
    load();
    const timer = anyRunning ? setInterval(load, POLL_INTERVAL_MS) : undefined;
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [enabled, anyRunning, reloads]);

  // A job that left the open list has succeeded: the overview is stale.
  const previousIds = useRef(new Set<string>());
  useEffect(() => {
    const ids = new Set(jobs.map((job) => job.id));
    if ([...previousIds.current].some((id) => !ids.has(id))) {
      onFinishedRef.current();
    }
    previousIds.current = ids;
  }, [jobs, onFinishedRef]);

  const refresh = useCallback(() => setReloads((n) => n + 1), []);
  const jobsByApp = Object.fromEntries(jobs.map((job) => [job.appId, job]));
  return { jobsByApp, refresh };
}

function StepIcon({ step }: { step: DeletionStep }) {
  if (step.status === "done") {
    return <CheckCircleRoundedIcon fontSize="small" color="success" />;
  }
  if (step.status === "failed") {
    return <ErrorRoundedIcon fontSize="small" color="error" />;
  }
  if (step.status === "running") return <CircularProgress size={18} />;
  return (
    <RadioButtonUncheckedRoundedIcon fontSize="small" sx={{ color: MUTED }} />
  );
}

function elapsed(step: DeletionStep): string {
  if (!step.startedAt) return "";
  const end = step.finishedAt ? Date.parse(step.finishedAt) : Date.now();
  return `${Math.max(0, Math.round((end - Date.parse(step.startedAt)) / 1000))}s`;
}

function PlanView({
  plan,
  language,
  confirmText,
  onConfirmText,
}: {
  plan: DeletionPlan;
  language: SupportedLanguage;
  confirmText: string;
  onConfirmText: (value: string) => void;
}) {
  const t = (key: string, params?: Record<string, string | number>) =>
    getLocalizedMessage(language, `appDeletionDialog.${key}`, params);
  const blocked = plan.blockers.length > 0;

  return (
    <Stack spacing={2}>
      {blocked && (
        <Alert severity="error">
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            {t("blocked")}
          </Typography>
          {plan.blockers.map((blocker) => (
            <Typography key={blocker} variant="body2">
              • {blocker}
            </Typography>
          ))}
        </Alert>
      )}

      {!blocked && (
        <Alert severity="warning">
          <Typography variant="body2">{t("irreversible")}</Typography>
          <Typography variant="body2">
            {t("membersLosingAccess", {
              count: plan.impact.membersLosingAccess,
            })}
          </Typography>
          {plan.impact.includedSlotMovesTo && (
            <Typography variant="body2">
              {t("includedSlotMovesTo", {
                appName: plan.impact.includedSlotMovesTo,
              })}
            </Typography>
          )}
          <Typography variant="body2">{t("usersKept")}</Typography>
        </Alert>
      )}

      {plan.warnings.map((warning) => (
        <Alert key={warning} severity="info">
          {warning}
        </Alert>
      ))}

      {SYSTEM_ORDER.map((system) => {
        const targets = plan.targets.filter((t) => t.system === system);
        if (targets.length === 0) return null;
        return (
          <Box key={system}>
            <Typography
              sx={{ color: MUTED, fontSize: "0.75rem", fontWeight: 700 }}
            >
              {t(`system.${system}`).toUpperCase()} · {targets.length}
            </Typography>
            {targets.map((target) => (
              <Box
                key={`${target.kind}:${target.id}`}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  py: 0.75,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: INK,
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      wordBreak: "break-all",
                    }}
                  >
                    {target.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: MUTED }}>
                    {[target.detail, target.id].filter(Boolean).join(" · ")}
                  </Typography>
                </Box>
                <Pill tone="red" label={t(`kind.${target.kind}`)} />
              </Box>
            ))}
          </Box>
        );
      })}

      {!blocked && (
        <TextField
          size="small"
          fullWidth
          autoComplete="off"
          label={t("typeToConfirm", { confirmName: plan.confirmName })}
          value={confirmText}
          onChange={(e) => onConfirmText(e.target.value)}
        />
      )}
    </Stack>
  );
}

function ProgressView({
  job,
  language,
}: {
  job: AppDeletionJob;
  language: SupportedLanguage;
}) {
  const t = (key: string) =>
    getLocalizedMessage(language, `appDeletionDialog.${key}`);
  return (
    <Stack spacing={1.5}>
      {job.status === "succeeded" && (
        <Alert severity="success">{t("succeeded")}</Alert>
      )}
      {job.status === "failed" && <Alert severity="error">{t("failed")}</Alert>}
      <Box role="status" aria-live="polite">
        {job.steps.map((step) => (
          <Box
            key={step.key}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              py: 1,
              borderBottom: `1px solid ${BORDER}`,
            }}
          >
            <Box sx={{ pt: 0.25 }}>
              <StepIcon step={step} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ color: INK, fontWeight: 600, fontSize: "0.85rem" }}
              >
                {t(`steps.${step.key.replace(".", "-")}`)}
              </Typography>
              {(step.error || step.note) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: step.error ? "error.main" : MUTED,
                    wordBreak: "break-word",
                  }}
                >
                  {step.error ?? step.note}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" sx={{ color: MUTED }}>
              {elapsed(step)}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: MUTED }}>
        {getLocalizedMessage(language, "appDeletionDialog.requestedBy", {
          email: job.requestedBy,
        })}
      </Typography>
    </Stack>
  );
}

/**
 * Lists everything deleting an app removes, asks for the typed name, then
 * shows the deletion steps live. With an open job it opens on the progress.
 */
export function AppDeletionDialog({
  target,
  openJob,
  onClose,
  onJobChanged,
  language,
}: {
  target: AppDeletionTarget | null;
  openJob?: AppDeletionJob;
  onClose: () => void;
  onJobChanged: () => void;
  language: SupportedLanguage;
}) {
  // No backdrop/Escape close: a destructive dialog closes through its buttons only.
  return (
    <Dialog open={!!target} fullWidth maxWidth="md">
      {target && (
        <DeletionDialogBody
          key={target.appId}
          target={target}
          openJob={openJob}
          onClose={onClose}
          onJobChanged={onJobChanged}
          language={language}
        />
      )}
    </Dialog>
  );
}

function DeletionDialogBody({
  target,
  openJob,
  onClose,
  onJobChanged,
  language,
}: {
  target: AppDeletionTarget;
  openJob?: AppDeletionJob;
  onClose: () => void;
  onJobChanged: () => void;
  language: SupportedLanguage;
}) {
  const [job, setJob] = useState<AppDeletionJob | null>(openJob ?? null);
  const [needsPlan] = useState(!openJob);
  const [plan, setPlan] = useState<DeletionPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const onJobChangedRef = useLatest(onJobChanged);
  const t = (key: string, params?: Record<string, string | number>) =>
    getLocalizedMessage(language, `appDeletionDialog.${key}`, params);

  useEffect(() => {
    if (!needsPlan) return;
    let cancelled = false;
    void fetchDeletionPlan(target.appId).then((result) => {
      if (cancelled) return;
      if ("data" in result) setPlan(result.data);
      else setError(result.error);
    });
    return () => {
      cancelled = true;
    };
  }, [needsPlan, target.appId]);

  const jobId = job?.id;
  const running = job?.status === "running";
  useEffect(() => {
    if (!jobId || !running) return;
    let cancelled = false;
    const timer = setInterval(
      () =>
        void fetchDeletionJob(jobId).then((result) => {
          if (cancelled || !("data" in result)) return;
          setJob(result.data);
          if (result.data.status !== "running") onJobChangedRef.current();
        }),
      POLL_INTERVAL_MS
    );
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [jobId, running, onJobChangedRef]);

  const start = async () => {
    if (!plan) return;
    setBusy(true);
    setError(null);
    const result = await startAppDeletion(target.appId, {
      fingerprint: plan.fingerprint,
      confirmName: confirmText.trim(),
    });
    setBusy(false);
    if ("data" in result) {
      setJob(result.data);
      onJobChanged();
    } else {
      setError(result.error);
    }
  };

  const resume = async () => {
    if (!job) return;
    setBusy(true);
    setError(null);
    const result = await resumeAppDeletion(job.id);
    setBusy(false);
    if ("data" in result) {
      setJob(result.data);
      onJobChanged();
    } else {
      setError(result.error);
    }
  };

  const canStart =
    !!plan &&
    plan.blockers.length === 0 &&
    confirmText.trim() === plan.confirmName &&
    !busy;

  return (
    <>
      <DialogTitle sx={{ color: INK, fontWeight: 700 }}>
        {t("title", { appName: `${target.clientName}/${target.appName}` })}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {job ? (
          <ProgressView job={job} language={language} />
        ) : plan ? (
          <PlanView
            plan={plan}
            language={language}
            confirmText={confirmText}
            onConfirmText={setConfirmText}
          />
        ) : (
          !error && (
            <Stack alignItems="center" sx={{ py: 4 }} spacing={1}>
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ color: MUTED }}>
                {t("loadingPlan")}
              </Typography>
            </Stack>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running || busy}>
          {getLocalizedMessage(
            language,
            job ? "dialogActions.close" : "dialogActions.cancel"
          )}
        </Button>
        {!job && plan && plan.blockers.length === 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={start}
            disabled={!canStart}
          >
            {busy ? t("starting") : t("deleteForever")}
          </Button>
        )}
        {job?.status === "failed" && (
          <Button
            variant="contained"
            color="error"
            onClick={resume}
            disabled={busy}
          >
            {t("resume")}
          </Button>
        )}
      </DialogActions>
    </>
  );
}
